// 1 · Floor & Tables (Main.dc.html; waiter / host iPad 1180×820, responsive).
// The floor is GET /api/dining/floor - every table's state is DERIVED on the
// server (never stored, never guessed here); the event stream says when to
// read it again.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PaymentStatusPanel from '@/components/payments/PaymentStatusPanel';
import { checkoutPanelFetcher } from '@/components/payments/usePaymentStatusPanel';
import { getAuthToken } from '@/lib/authToken';
import { api, errorText, newKey } from '../lib/api';
import { clock, initials, money, plural, shortName, todayLabel } from '../lib/format';
import { useLive } from '../lib/useLive';
import { useDiningSession } from '../lib/session';
import { useSoundPref } from '../lib/sound';
import { C, FONT_TIGHT, LINE_CHIP_DARK, TABLE_STATE, TABLE_STATE_ORDER } from '../theme/tokens';
import { AlertBell, AlertCenter, alertsEnabledFor, useAlerts } from '../components/AlertCenter';
import { BottomNav, DarkTopBar, UserMenu } from '../components/Shell';
import { Btn, Clock, Empty, ErrorNote, FullError, IconBtn, LiveIndicator, Skeleton, StreamBanner, useToast } from '../components/ui';
import {
  IconAlert, IconArrowRight, IconBell, IconCalendar, IconCard, IconClock, IconClose, IconPencil, IconPlus, IconTables, IconUsers,
} from '../components/icons';
import ReservationsPanel from './ReservationsPanel';

const REGISTER_KEY = 'dining_register_terminal_id';

function registerTerminal() {
  try { return Number(localStorage.getItem(REGISTER_KEY)) || null; } catch (e) { return null; }
}

// ------------------------------------------------------------------ layout

function sizeOf(t) {
  const cap = t.capacity || 4;
  const shape = String(t.shape || 'round').toLowerCase();
  if (shape.startsWith('round') || shape === 'circle') {
    const d = cap <= 2 ? 84 : cap <= 4 ? 100 : 116;
    return { w: d, h: d, shape: 'round' };
  }
  if (shape.startsWith('booth')) return { w: 112, h: 92, shape: 'booth' };
  if (shape.startsWith('sq')) return { w: cap <= 2 ? 80 : 88, h: cap <= 2 ? 80 : 88, shape: 'sq' };
  return { w: cap <= 4 ? 140 : cap <= 6 ? 160 : 180, h: cap <= 6 ? 88 : 96, shape: 'rect' };
}

// Positions from the saved floor plan when it has them, else a tidy grid.
function layoutTables(tables, width) {
  const placed = tables.filter((t) => (t.pos_x || 0) !== 0 || (t.pos_y || 0) !== 0);
  const usePlan = tables.length > 1 && placed.length >= Math.ceil(tables.length * 0.6);
  const cellW = 210;
  const cellH = 180;
  const cols = Math.max(1, Math.floor((width - 40) / cellW));
  return tables.map((t, i) => {
    const s = sizeOf(t);
    if (usePlan) {
      const w = Math.max(s.w * 0.8, t.width || s.w);
      const h = Math.max(s.h * 0.8, t.height || s.h);
      return { t, x: t.pos_x || 0, y: t.pos_y || 0, w: s.shape === 'round' ? Math.max(w, h) : w, h: s.shape === 'round' ? Math.max(w, h) : h, shape: s.shape };
    }
    const col = i % cols;
    const row = Math.floor(i / cols);
    return { t, x: col * cellW + (cellW - s.w) / 2, y: row * cellH + (cellH - s.h) / 2, w: s.w, h: s.h, shape: s.shape };
  });
}

function chairsFor(p) {
  const cap = Math.min(p.t.capacity || 4, 12);
  const out = [];
  if (p.shape === 'round') {
    const cx = p.x + p.w / 2;
    const cy = p.y + p.h / 2;
    const r = p.w / 2 + 12;
    for (let i = 0; i < cap; i += 1) {
      const a = -90 + (i * 360) / cap;
      const rad = (a * Math.PI) / 180;
      out.push({ l: cx + r * Math.cos(rad) - 14, t: cy + r * Math.sin(rad) - 6, w: 28, h: 12, rot: a + 90 });
    }
  } else if (p.shape === 'booth') {
    out.push({ l: p.x - 24, t: p.y + 2, w: 16, h: p.h - 4 });
    out.push({ l: p.x + p.w + 8, t: p.y + 2, w: 16, h: p.h - 4 });
  } else {
    const top = Math.ceil(cap / 2);
    const bot = cap - top;
    for (let i = 0; i < top; i += 1) out.push({ l: p.x + (p.w * (i + 1)) / (top + 1) - 15, t: p.y - 18, w: 30, h: 12 });
    for (let i = 0; i < bot; i += 1) out.push({ l: p.x + (p.w * (i + 1)) / (bot + 1) - 15, t: p.y + p.h + 6, w: 30, h: 12 });
  }
  return out;
}

// a reservation's "19:30" is already the restaurant's wall clock: shown as-is, "7:30 PM"
function reservationTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).split(':').map(Number);
  const hour = (h || 0) % 24;
  return `${hour % 12 || 12}:${String(m || 0).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

// ------------------------------------------------------------------ one table

function Badge({ bg, fg, children, pulse }) {
  return (
    <span className={pulse ? 'c29-pulse' : undefined} style={{ position: 'absolute', top: -15, left: -13, width: 32, height: 32, borderRadius: '50%', background: bg, color: fg, border: `2.5px solid ${C.floor}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </span>
  );
}

function TableShape({ p, selected, dim, onPick }) {
  const t = p.t;
  const st = TABLE_STATE[t.status] || TABLE_STATE.available;
  const occ = !!t.session;
  const s = t.session;
  const radius = p.shape === 'round' ? '50%' : p.shape === 'booth' ? 12 : 16;
  const aria = `Table ${t.table_number}, ${st.label}${s ? `, ${s.party_size} guests, ${s.seated_minutes ?? 0} minutes` : ''}`;
  return (
    <button type="button" onClick={() => onPick(t)} aria-label={aria} aria-pressed={selected} className="c29-press"
      style={{
        position: 'absolute', left: p.x, top: p.y, width: p.w, height: p.h, padding: 0, borderRadius: radius,
        background: st.fill, border: `2px solid ${st.ring}`, color: st.text, opacity: dim ? 0.28 : 1,
        boxShadow: selected ? `0 0 0 5px ${C.floor}, 0 0 0 8px ${C.orange}` : 'none',
      }}>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 1, lineHeight: 1.1 }}>
        <span style={{ fontFamily: FONT_TIGHT, fontSize: p.w >= 100 ? 26 : 22, fontWeight: 800, letterSpacing: '-0.5px' }}>{t.table_number}</span>
        {occ && (
          <>
            <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.88, display: 'flex', alignItems: 'center', gap: 4 }}>
              <IconUsers size={12} sw={2.4} />{s.party_size}{s.server_name ? ` · ${initials(s.server_name)}` : ''}
            </span>
            {t.open_balance_cents != null && t.open_balance_cents > 0 && <span style={{ fontSize: 13, fontWeight: 800, marginTop: 2 }}>{money(t.open_balance_cents)}</span>}
          </>
        )}
        {!occ && <span style={{ fontSize: 11, fontWeight: 600, color: C.greenText, opacity: 0.8 }}>{t.capacity || '—'} seats</span>}
      </span>
      {occ && (
        <span style={{ position: 'absolute', top: -13, right: -12, height: 24, padding: '0 8px', borderRadius: 12, background: C.floor, border: `1.5px solid ${st.ring}`, color: '#F1F5F9', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconClock size={12} sw={2.4} />{s.seated_minutes ?? 0}m
        </span>
      )}
      {!occ && t.reservation && (
        <span style={{ position: 'absolute', bottom: -13, left: '50%', transform: 'translateX(-50%)', height: 24, padding: '0 8px', borderRadius: 12, background: C.floor, border: `1.5px solid ${C.violetHi}`, color: '#C4B5FD', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          {reservationTime(t.reservation.time)} · {t.reservation.party_size}
        </span>
      )}
      {t.status === 'food_ready' && <Badge bg={C.amber} fg="#1F1300" pulse><IconBell size={16} sw={2.4} /></Badge>}
      {t.status === 'checkout_requested' && <Badge bg={C.violetHi} fg="#FFFFFF"><IconCard size={16} sw={2.2} /></Badge>}
      {t.status === 'needs_attention' && <Badge bg={C.redHi} fg="#FFFFFF"><IconAlert size={16} sw={2.4} /></Badge>}
      {t.status === 'ordering' && <Badge bg={C.blueHi} fg="#FFFFFF"><IconPencil size={15} sw={2.4} /></Badge>}
    </button>
  );
}

function FloorCanvas({ tables, selectedId, pick, mode, onPick }) {
  const box = useRef(null);
  const [dims, setDims] = useState({ w: 832, h: 600 });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setDims({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => ro && ro.disconnect();
  }, []);
  const placed = useMemo(() => layoutTables(tables, dims.w), [tables, dims.w]);
  const bounds = placed.reduce((b, p) => ({
    minX: Math.min(b.minX, p.x - 30), minY: Math.min(b.minY, p.y - 30),
    maxX: Math.max(b.maxX, p.x + p.w + 30), maxY: Math.max(b.maxY, p.y + p.h + 30),
  }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
  const bw = bounds.maxX - bounds.minX;
  const bh = bounds.maxY - bounds.minY;
  const scale = placed.length ? Math.min((dims.w - 48) / bw, (dims.h - 96) / bh, 1.25) : 1;
  const offX = placed.length ? (dims.w - bw * scale) / 2 - bounds.minX * scale : 0;
  const offY = placed.length ? Math.max(24, (dims.h - 70 - bh * scale) / 2) - bounds.minY * scale : 0;
  return (
    <div ref={box} className="c29-dots" style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', transform: `translate(${offX}px, ${offY}px) scale(${scale})` }}>
        {placed.map((p) => chairsFor(p).map((c, i) => (
          <span key={`${p.t.id}-c${i}`} style={{ position: 'absolute', left: c.l, top: c.t, width: c.w, height: c.h, borderRadius: 6, background: (TABLE_STATE[p.t.status] || TABLE_STATE.available).chair, transform: c.rot != null ? `rotate(${c.rot}deg)` : undefined, opacity: mode && !pick(p.t) ? 0.28 : 1 }} />
        )))}
        {placed.map((p) => (
          <TableShape key={p.t.id} p={p} selected={p.t.id === selectedId} dim={!!mode && !pick(p.t) && p.t.id !== selectedId} onPick={onPick} />
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ side sheet

function Stat({ label, value, big }) {
  return (
    <div style={{ background: C.tile, borderRadius: 12, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span className="c29-caps" style={{ color: C.textDim }}>{label}</span>
      <span style={{ fontSize: big ? 20 : 17, fontWeight: 700, color: C.textHi, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
    </div>
  );
}

function roundChip(round, ticketsByItem) {
  const states = round.items.filter((i) => i.status !== 'voided').map((i) => ticketsByItem[i.id]?.status || 'new');
  if (!states.length) return LINE_CHIP_DARK.cancelled;
  if (states.every((s) => s === 'served')) return LINE_CHIP_DARK.served;
  if (states.some((s) => s === 'ready')) return LINE_CHIP_DARK.ready;
  if (states.some((s) => s === 'preparing')) return LINE_CHIP_DARK.preparing;
  return LINE_CHIP_DARK.new;
}

function OccupiedSheet({ table, me, perms, onMode, onChanged }) {
  const navigate = useNavigate();
  const toast = useToast();
  const sid = table.session.id;
  const orderId = table.session.order_id;
  const canKds = perms.can('kds.read');
  const view = useLive(async () => {
    const [session, tickets] = await Promise.all([
      api.get(`/dining/sessions/${sid}`),
      canKds && orderId ? api.get('/dining/kitchen/tickets', { order_id: orderId, view: 'all' }).catch(() => null) : null,
    ]);
    return { session, tickets: tickets?.tickets || null };
  }, [sid, orderId, canKds], {
    match: (e) => e.payload?.session_id === sid || e.payload?.order_id === orderId || e.payload?.ticket?.order_id === orderId,
  });
  const [busy, setBusy] = useState(null);
  const s = table.session;
  const st = TABLE_STATE[table.status] || TABLE_STATE.available;
  const data = view.data;
  const ticketsByItem = {};
  (data?.tickets || []).forEach((tk) => tk.items.forEach((it) => { ticketsByItem[it.order_item_id] = { ...it, ticket: tk }; }));
  const ready = (data?.tickets || []).filter((tk) => tk.status === 'ready');
  const readyItems = ready.flatMap((tk) => tk.items.filter((i) => !i.voided && i.status === 'ready'));
  const checks = (data?.session?.checks || []).filter((c) => c.status !== 'void');
  const paidChecks = checks.filter((c) => c.status === 'paid').length;
  const totals = data?.session?.totals;
  const draftCount = table.items?.draft || 0;
  const nothingOrdered = !(table.items?.sent) && !draftCount;
  const canRelease = perms.can('tables.release') && draftCount === 0 && (nothingOrdered || table.open_balance_cents === 0);

  const serve = async () => {
    setBusy('serve');
    try {
      for (const tk of ready) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/dining/kitchen/tickets/${tk.id}/serve`, { expected_version: tk.version }, { idempotencyKey: newKey('serve') });
      }
      toast.ok(`Table ${table.table_number} · marked served`);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      view.reload();
      onChanged();
    }
  };

  const release = async () => {
    setBusy('release');
    try {
      await api.post(`/dining/sessions/${sid}/release`, { expected_version: s.version });
      toast.ok(`Table ${table.table_number} released`);
      onChanged(true);
    } catch (e) {
      toast.error(errorText(e));
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const balanceSub = checks.length
    ? `${plural(checks.length, 'check')} · ${paidChecks ? `${paidChecks} paid` : 'nothing paid'}`
    : (totals && !totals.error && totals.paid_cents > 0 ? `${money(totals.paid_cents)} paid` : '1 bill · nothing paid');

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        <Stat label="Guests" value={s.party_size} big />
        <Stat label="Server" value={shortName(s.server_name) || '—'} />
        <Stat label="Seated" value={clock(s.opened_at) || '—'} />
        <Stat label="At table" value={`${s.seated_minutes ?? 0} min`} big />
      </div>

      {readyItems.length > 0 && (
        <div style={{ borderRadius: 14, background: 'rgba(245,158,11,.12)', border: `1.5px solid ${C.amber}`, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.amberHi, fontSize: 14, fontWeight: 700 }}>
            <IconBell size={16} sw={2.4} />{plural(readyItems.length, 'item')} ready at the pass{ready[0]?.ready_at ? ` · ${clock(ready[0].ready_at)}` : ''}
          </div>
          {readyItems.slice(0, 5).map((i) => (
            <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14, color: '#F1F5F9' }}>
              <span>{i.quantity > 1 ? `${i.quantity}× ` : ''}{i.name}</span><span style={{ color: C.textMid, flex: 'none' }}>{i.seat_number ? `Seat ${i.seat_number}` : ''}</span>
            </div>
          ))}
          {perms.can('orders.serve') && (
            <button type="button" className="c29-press" onClick={serve} disabled={!!busy}
              style={{ height: 48, border: 'none', borderRadius: 12, background: C.amber, color: '#1F1300', fontSize: 15, fontWeight: 700 }}>
              {busy === 'serve' ? 'Marking…' : 'Mark served'}
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0 }}>
        <span className="c29-caps" style={{ color: C.textDim }}>Order</span>
        {view.loading && !data && <Skeleton height={72} />}
        {view.error && !data && <ErrorNote error={view.error} onRetry={view.reload} compact />}
        {data && (
          <div className="c29-scroll" style={{ maxHeight: 180 }}>
            {data.session.rounds.map((r) => {
              const chip = roundChip(r, ticketsByItem);
              const live = r.items.filter((i) => i.status !== 'voided').length;
              return (
                <div key={r.round_no} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 36, borderBottom: `1px solid ${C.line}` }}>
                  <span style={{ fontSize: 13.5, color: '#D5DBE3' }}>Round {r.round_no} · {clock(r.sent_at)} · {plural(live, 'item')}</span>
                  <span style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', background: chip.bg, color: chip.fg }}>{chip.label}</span>
                </div>
              );
            })}
            {data.session.draft.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 36, borderBottom: `1px solid ${C.line}` }}>
                <span style={{ fontSize: 13.5, color: '#D5DBE3' }}>{plural(data.session.draft.length, 'item')} on the pad</span>
                <span style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', background: LINE_CHIP_DARK.draft.bg, color: LINE_CHIP_DARK.draft.fg }}>Not sent</span>
              </div>
            )}
            {!data.session.rounds.length && !data.session.draft.length && (
              <div style={{ fontSize: 13.5, color: C.textLo, padding: '8px 0' }}>Nothing ordered yet.</div>
            )}
            {s.status === 'checkout_requested' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 36 }}>
                <span style={{ fontSize: 13.5, color: '#D5DBE3' }}>Check requested {clock(s.checkout_requested_at)}</span>
                <span style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', background: 'rgba(139,92,246,.2)', color: '#C4B5FD' }}>Check</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span className="c29-caps" style={{ color: C.textDim }}>Balance</span>
          <span style={{ fontSize: 12.5, color: C.textLo }}>{table.balance_error ? String(table.balance_error) : balanceSub}</span>
        </div>
        <span style={{ fontFamily: FONT_TIGHT, fontSize: 34, fontWeight: 800, color: C.textHi, letterSpacing: '-0.5px' }}>{money(table.open_balance_cents ?? 0)}</span>
      </div>

      <div style={{ flex: 1 }} />
      {(perms.can('orders.create') || perms.can('orders.read')) && (
        <button type="button" className="c29-press" onClick={() => navigate(`/dining/table/${sid}`)}
          style={{ height: 56, borderRadius: 14, border: 'none', background: C.orange, color: C.orangeDeep, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {perms.can('orders.create') ? 'Open order' : 'View order'}<IconArrowRight size={18} />
        </button>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${perms.can('payments.take') ? 4 : 3}, minmax(0, 1fr))`, gap: 8 }}>
        <Btn kind="dark" disabled={!perms.can('tables.transfer')} onClick={() => onMode({ kind: 'transfer', from: table })}>Transfer</Btn>
        <Btn kind="dark" disabled={!perms.can('tables.merge')} onClick={() => onMode({ kind: 'merge', from: table })}>Merge</Btn>
        {perms.can('payments.take') && <Btn kind="dark" onClick={() => navigate(`/dining/checkout/${sid}`)}>Checkout</Btn>}
        <Btn kind={canRelease ? 'dark' : 'darkLocked'} disabled={!canRelease} busy={busy === 'release'} onClick={release}
          style={canRelease ? { borderColor: C.greenHi, color: C.greenText } : undefined}>Release</Btn>
      </div>
      <span style={{ fontSize: 12, color: C.textDim, textAlign: 'center' }}>
        {canRelease ? (nothingOrdered ? 'Nothing was ordered - the table can be released.' : 'Everything is paid - release the table for the next guests.')
          : draftCount ? 'Send or remove the items still on the pad first.' : 'Release unlocks when every check on this table is paid.'}
      </span>
    </>
  );
}

function AvailableSheet({ table, perms, onSeated }) {
  const toast = useToast();
  const [party, setParty] = useState(Math.min(2, table.capacity || 2));
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(null);
  const res = table.reservation;
  useEffect(() => { setParty(Math.min(2, table.capacity || 2)); keyRef.current = null; }, [table.id, table.capacity]);
  const maxPick = Math.max(4, Math.min(8, table.capacity || 4));
  const seat = async (withReservation) => {
    setBusy(withReservation ? 'res' : 'walk');
    keyRef.current = keyRef.current || newKey('seat');
    try {
      const body = withReservation ? { party_size: res.party_size || party, reservation_id: res.reservation_id } : { party_size: party };
      const answer = await api.post(`/dining/tables/${table.id}/seat`, body, { idempotencyKey: keyRef.current });
      keyRef.current = null;
      toast.ok(answer.already_open ? `Table ${table.table_number} was already seated` : `Table ${table.table_number} · ${plural(answer.session.party_size, 'guest')} seated`);
      onSeated(answer);
    } catch (e) {
      if (!e.network) keyRef.current = null;
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.5 }}>
        {table.capacity || '—'} seats{table.section ? ` · ${table.section}` : ''}. No one is seated here.
      </div>
      {res && (
        <div style={{ borderRadius: 14, background: 'rgba(139,92,246,.12)', border: `1.5px solid ${C.violetHi}`, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#C4B5FD' }}>Reservation · {reservationTime(res.time)}</span>
          <span style={{ fontSize: 17, fontWeight: 700, color: C.textHi }}>{res.customer_name || 'Guest'} · {plural(res.party_size || 0, 'guest')}</span>
          {perms.can('tables.seat') && (
            <Btn kind="dark" busy={busy === 'res'} onClick={() => seat(true)} style={{ borderColor: C.violetHi, color: '#E9D5FF', height: 48 }}>Seat this reservation</Btn>
          )}
        </div>
      )}
      {perms.can('tables.seat') ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="c29-caps" style={{ color: C.textDim }}>Party size</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
              {Array.from({ length: maxPick }, (_, i) => i + 1).map((n) => {
                const on = n === party;
                return (
                  <button key={n} type="button" className="c29-press" aria-pressed={on} onClick={() => setParty(n)}
                    style={{ height: 52, borderRadius: 12, border: on ? `2px solid ${C.orange}` : `1px solid ${C.line2}`, background: on ? 'rgba(249,115,22,.12)' : C.panel2, color: on ? '#FDBA74' : C.text, fontSize: 18, fontWeight: 700 }}>{n}</button>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: C.textLo, flex: 1 }}>Larger party</span>
              <IconBtn dark label="One fewer guest" onClick={() => setParty((p) => Math.max(1, p - 1))}>−</IconBtn>
              <span className="c29-tight" style={{ width: 36, textAlign: 'center', fontSize: 18, fontWeight: 800, color: C.textHi }}>{party}</span>
              <IconBtn dark label="One more guest" onClick={() => setParty((p) => Math.min(99, p + 1))}>+</IconBtn>
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <Btn kind="green" busy={busy === 'walk'} onClick={() => seat(false)} full>Seat {plural(party, 'guest')}</Btn>
        </>
      ) : (
        <div style={{ fontSize: 13, color: C.textLo }}>Your role does not seat guests.</div>
      )}
    </>
  );
}

function SideSheet({ table, me, perms, onClose, onMode, onChanged, onSeated, attention }) {
  if (!table) {
    return (
      <>
        <h2 style={{ margin: 0, fontFamily: FONT_TIGHT, fontSize: 26, fontWeight: 800, color: C.textHi }}>Tables</h2>
        <span style={{ fontSize: 14, color: C.textMid, lineHeight: 1.5 }}>Tap a table to seat guests, open its order or move the party.</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <span className="c29-caps" style={{ color: C.textDim }}>Needs you</span>
          {!attention.length && <span style={{ fontSize: 13.5, color: C.textLo }}>Nothing waiting. Ready food, check requests and problems show here.</span>}
          {attention.map((t) => {
            const st = TABLE_STATE[t.status];
            return (
              <button key={t.id} type="button" className="c29-press" onClick={() => onChanged(false, t)}
                style={{ height: 56, borderRadius: 12, border: `1px solid ${C.line2}`, background: C.panel2, color: C.text, display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px' }}>
                <span style={{ width: 34, height: 28, borderRadius: 8, background: st.fill, border: `2px solid ${st.ring}`, color: st.text, fontFamily: FONT_TIGHT, fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{t.table_number}</span>
                <span style={{ flex: 1, textAlign: 'left', fontSize: 14, fontWeight: 600 }}>{st.label}</span>
                <span style={{ fontSize: 12.5, color: C.textLo }}>{t.session?.seated_minutes ?? 0}m</span>
              </button>
            );
          })}
        </div>
      </>
    );
  }
  const st = TABLE_STATE[table.status] || TABLE_STATE.available;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h2 style={{ margin: 0, fontFamily: FONT_TIGHT, fontSize: 30, fontWeight: 800, color: C.textHi, letterSpacing: '-0.5px' }}>Table {table.table_number}</h2>
          <span style={{ alignSelf: 'flex-start', height: 26, padding: '0 10px', borderRadius: 13, background: st.chipBg, color: st.chipFg, fontSize: 12.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: st.ring }} />{st.label}
          </span>
        </div>
        <IconBtn dark label="Close" onClick={onClose}><IconClose size={18} /></IconBtn>
      </div>
      {table.session
        ? <OccupiedSheet key={table.session.id} table={table} me={me} perms={perms} onMode={onMode} onChanged={onChanged} />
        : <AvailableSheet table={table} perms={perms} onSeated={onSeated} />}
    </>
  );
}

// ------------------------------------------------------------------ the screen

export default function FloorScreen() {
  const { me, perms } = useDiningSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const floor = useLive(() => api.get('/dining/floor'), []);
  const alertsState = useAlerts({ enabled: alertsEnabledFor(me) });
  const [areaId, setAreaId] = useState(null);
  const [mode, setMode] = useState(null); // {kind: transfer|merge, from: table}
  const [confirm, setConfirm] = useState(null);
  const [tray, setTray] = useState(false);
  const [bookOpen, setBookOpen] = useState(() => params.get('panel') === 'reservations');
  const [soundOn] = useSoundPref();
  const selectedId = Number(params.get('table')) || null;
  const select = useCallback((id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('table', String(id)); else next.delete('table');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const data = floor.data;
  const areas = useMemo(() => {
    if (!data) return [];
    const list = data.areas.map((a) => ({ ...a, tables: data.tables.filter((t) => t.dining_area_id === a.id) }));
    const loose = data.tables.filter((t) => !data.areas.some((a) => a.id === t.dining_area_id));
    if (loose.length) list.push({ id: 'none', name: list.length ? 'Other' : 'Main floor', tables: loose });
    return list.filter((a) => a.tables.length || a.id !== 'none');
  }, [data]);
  const area = areas.find((a) => a.id === areaId) || areas[0];
  useEffect(() => {
    if (!selectedId || !data) return;
    const t = data.tables.find((x) => x.id === selectedId);
    if (t) {
      const owner = areas.find((a) => a.tables.some((x) => x.id === t.id));
      if (owner && owner.id !== area?.id) setAreaId(owner.id);
    }
  }, [selectedId, data, areas, area]);
  const tables = area?.tables || [];
  const selected = data?.tables.find((t) => t.id === selectedId) || null;
  const counts = {};
  tables.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });
  const covers = tables.reduce((n, t) => n + (t.session?.party_size || 0), 0);
  const attention = (data?.tables || []).filter((t) => ['food_ready', 'checkout_requested', 'needs_attention'].includes(t.status));

  const pickable = (t) => {
    if (!mode) return true;
    if (mode.kind === 'transfer') return !t.session && t.id !== mode.from.id;
    return !!t.session && t.id !== mode.from.id;
  };

  const onPick = (t) => {
    if (mode) {
      if (!pickable(t)) return;
      setConfirm({ ...mode, to: t });
      return;
    }
    select(t.id === selectedId ? null : t.id);
  };

  const runMove = async () => {
    const { kind, from, to } = confirm;
    try {
      if (kind === 'transfer') {
        await api.post(`/dining/sessions/${from.session.id}/transfer`, { to_table_id: to.id, expected_version: from.session.version });
        toast.ok(`Table ${from.table_number} moved to Table ${to.table_number}`);
        select(to.id);
      } else {
        await api.post(`/dining/sessions/${from.session.id}/merge`, { into_session_id: to.session.id, expected_version: from.session.version });
        toast.ok(`Table ${from.table_number} merged into Table ${to.table_number}`);
        select(to.id);
      }
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setConfirm(null);
      setMode(null);
      floor.reload();
    }
  };

  const seatWalkIn = () => {
    const free = tables.filter((t) => !t.session).sort((a, b) => (a.capacity || 0) - (b.capacity || 0));
    const fit = free.find((t) => (t.capacity || 0) >= 2) || free[0];
    if (!fit) { toast.info(`No free table in ${area?.name || 'this area'}`); return; }
    select(fit.id);
  };

  const onSeated = (answer) => {
    floor.reload();
    if (perms.can('orders.create')) navigate(`/dining/table/${answer.session.id}`);
  };

  const panelFetcher = useMemo(() => checkoutPanelFetcher({ token: () => getAuthToken(), terminalId: registerTerminal() }), []);

  return (
    <div className="c29 c29-dark" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <DarkTopBar title={area?.name || 'Floor'} sub={[me?.restaurant_name, todayLabel()].filter(Boolean).join(' · ')}
        right={(
          <>
            {perms.can('reservations.read') && (
              <Btn kind="dark" height={48} icon={<IconCalendar size={18} />} onClick={() => setBookOpen(true)} aria-haspopup="dialog">Reservations</Btn>
            )}
            <LiveIndicator status={floor.streamStatus} />
            {perms.can('payments.take') && <PaymentStatusPanel fetchPanel={panelFetcher} canManage={['owner', 'manager'].includes(me?.role)} />}
            <Clock size={20} color={C.textHi} />
            {alertsState.enabled && <AlertBell count={alertsState.alerts.length} onClick={() => setTray(true)} />}
            <UserMenu me={me} />
          </>
        )}>
        {areas.length > 1 && (
          <div role="tablist" aria-label="Dining areas" className="c29-scroll" style={{ display: 'flex', gap: 4, padding: 4, background: C.panel2, borderRadius: 12, marginLeft: 12, minWidth: 0, overflowY: 'hidden' }}>
            {areas.map((a) => {
              const on = a.id === area?.id;
              return (
                <button key={a.id} type="button" role="tab" aria-selected={on} onClick={() => setAreaId(a.id)}
                  style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 9, background: on ? C.line2 : 'transparent', color: on ? C.textHi : C.textMid, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                  {a.name}<span style={{ fontSize: 12, color: on ? C.textMid : '#6B7684' }}>{a.tables.length}</span>
                </button>
              );
            })}
          </div>
        )}
      </DarkTopBar>
      <StreamBanner status={floor.streamStatus} onRetry={floor.reload} />

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <section style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <div className="c29-scroll" style={{ height: 52, flex: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '0 16px', borderBottom: `1px solid ${C.panel2}`, overflowY: 'hidden' }}>
            {TABLE_STATE_ORDER.filter((k) => k !== 'seated' || counts.seated).map((k) => (
              <span key={k} style={{ height: 32, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '0 10px', borderRadius: 8, background: C.panel, fontSize: 12.5, fontWeight: 600, color: '#C3CAD4', whiteSpace: 'nowrap', flex: 'none' }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: TABLE_STATE[k].ring }} />{TABLE_STATE[k].label}<span style={{ color: C.textHi, fontWeight: 800 }}>{counts[k] || 0}</span>
              </span>
            ))}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12.5, color: C.textLo, whiteSpace: 'nowrap' }}>{plural(covers, 'cover')}</span>
          </div>

          {mode && (
            <div className="c29-in" style={{ position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 5, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 8px 8px 16px', borderRadius: 14, background: C.ink, border: `1.5px solid ${C.orange}`, boxShadow: '0 16px 40px rgba(0,0,0,.4)', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: '#FFFFFF' }}>
                {mode.kind === 'transfer' ? `Tap a free table to move Table ${mode.from.table_number}` : `Tap the table Table ${mode.from.table_number} joins`}
              </span>
              <Btn kind="dark" height={40} onClick={() => setMode(null)}>Cancel</Btn>
            </div>
          )}

          {floor.loading && !data && (
            <div style={{ flex: 1, padding: 32, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 28, alignContent: 'start' }}>
              {Array.from({ length: 12 }, (_, i) => <Skeleton key={i} height={100} style={{ borderRadius: i % 3 === 0 ? 50 : 16 }} />)}
            </div>
          )}
          {floor.error && !data && <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FullError error={floor.error} onRetry={floor.reload} title="Could not load the floor" /></div>}
          {data && !data.tables.length && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Empty icon={<IconTables size={30} />} title="No tables yet" body="The owner adds tables in Back Office → Tables & Reservations. They appear here as soon as they are saved." />
            </div>
          )}
          {data && data.tables.length > 0 && (
            <FloorCanvas tables={tables} selectedId={selectedId} mode={mode} pick={pickable} onPick={onPick} />
          )}
          {floor.error && data && <div style={{ position: 'absolute', left: 16, right: 16, bottom: 88 }}><ErrorNote error={`Showing the last floor - ${errorText(floor.error)}`} onRetry={floor.reload} compact /></div>}

          {data && perms.can('tables.seat') && !mode && tables.some((t) => !t.session) && (
            <button type="button" className="c29-press" onClick={seatWalkIn}
              style={{ position: 'absolute', right: 20, bottom: 20, height: 52, padding: '0 20px', border: 'none', borderRadius: 14, background: C.orange, color: C.orangeDeep, fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 10px 30px rgba(249,115,22,.25)' }}>
              <IconPlus size={18} />Seat walk-in
            </button>
          )}
        </section>

        <aside className="c29-scroll" style={{ width: 348, flex: 'none', background: C.panel, borderLeft: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', padding: 20, gap: 16 }}>
          <SideSheet table={selected} me={me} perms={perms} attention={attention}
            onClose={() => select(null)} onMode={(m) => setMode(m)} onSeated={onSeated}
            onChanged={(released, pickTable) => {
              floor.reload();
              if (pickTable) select(pickTable.id);
              else if (released) select(null);
            }} />
        </aside>
      </div>

      <BottomNav me={me} active="floor" badges={{ expo: attention.filter((t) => t.status === 'food_ready').length }} />

      {confirm && (
        <div className="c29-fade" style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div role="dialog" aria-modal="true" aria-label="Confirm" className="c29-sheet" style={{ width: 440, maxWidth: '100%', background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 24, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.textHi }}>
              {confirm.kind === 'transfer' ? `Move Table ${confirm.from.table_number} to Table ${confirm.to.table_number}?` : `Merge Table ${confirm.from.table_number} into Table ${confirm.to.table_number}?`}
            </h2>
            <span style={{ fontSize: 14, color: C.textMid, lineHeight: 1.5 }}>
              {confirm.kind === 'transfer'
                ? 'The party, its order and its kitchen tickets move to the new table. The kitchen sees the new table at once.'
                : `Every item of Table ${confirm.from.table_number} joins Table ${confirm.to.table_number}'s bill, the guests add up and Table ${confirm.from.table_number} is freed. A table that already paid something cannot be merged.`}
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
              <Btn kind="dark" height={56} onClick={() => { setConfirm(null); }}>Back</Btn>
              <Btn kind="orange" height={56} onClick={runMove}>{confirm.kind === 'transfer' ? 'Move table' : 'Merge tables'}</Btn>
            </div>
          </div>
        </div>
      )}
      {bookOpen && (
        <ReservationsPanel perms={perms} refreshKey={data} onClose={() => setBookOpen(false)}
          onSeated={(r) => { floor.reload(); setBookOpen(false); if (r.table_id) select(r.table_id); }} />
      )}
      <AlertCenter me={me} state={alertsState} trayOpen={tray} onTray={setTray} soundOn={soundOn} streamStatus={floor.streamStatus} />
    </div>
  );
}
